import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="font-heading text-xl font-bold text-foreground-900">
            itaobuy<span className="text-primary-500">sheet</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className="flex items-center gap-1 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </Link>
          <a
            href="#categorie"
            className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Categorie
          </a>
          <a
            href="#guida"
            className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Guida
          </a>
          <Link
            to="/articolo/itaobuy-spreadsheet-guida-completa"
            className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Articoli
          </Link>
          <Link
            to="/articolo/itaobuy-spreadsheet-guida-acquisti"
            className="flex items-center gap-1 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            <i className="ri-shopping-bag-3-line text-base"></i>
            Guida Acquisti
          </Link>

          {/* Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>Prodotti</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg py-3 max-h-96 overflow-y-auto">
                <p className="px-4 pb-2 text-xs text-foreground-400 uppercase tracking-wider font-label">Categorie Prodotto</p>
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-1 inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-right-up-line text-sm"></i>
            Itaobuy Sheet
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 flex flex-col">
            {/* Home & quick nav */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-background-200">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2.5 bg-background-100 rounded-full text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-home-4-line text-base"></i>
                Home
              </Link>
              <Link
                to="/articolo/itaobuy-spreadsheet-guida-acquisti"
                className="flex items-center gap-2 px-4 py-2.5 bg-background-100 rounded-full text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-shopping-bag-3-line text-base"></i>
                Guida Acquisti
              </Link>
            </div>

            {/* Nav links */}
            <a
              href="#categorie"
              className="flex items-center gap-3 py-3.5 text-base text-foreground-700 border-b border-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-grid-line text-lg text-foreground-400"></i>
              Categorie
            </a>
            <a
              href="#guida"
              className="flex items-center gap-3 py-3.5 text-base text-foreground-700 border-b border-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-foreground-400"></i>
              Guida
            </a>
            <Link
              to="/articolo/itaobuy-spreadsheet-guida-completa"
              className="flex items-center gap-3 py-3.5 text-base text-foreground-700 border-b border-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-article-line text-lg text-foreground-400"></i>
              Articoli
            </Link>

            {/* Category grid on mobile - visually rich */}
            <p className="mt-5 mb-3 text-xs text-foreground-400 uppercase tracking-wider font-label">Categorie Prodotto</p>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-2.5">
                    <span className="text-xs font-medium text-foreground-700">{cat.name}</span>
                  </div>
                </a>
              ))}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 mb-4 flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-right-up-line text-sm"></i>
              Itaobuy Sheet
            </a>

            {/* Legal links */}
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2 pb-6 border-t border-background-100 pt-4">
              <Link to="/privacy" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors" onClick={() => setMobileOpen(false)}>Privacy</Link>
              <Link to="/cookie" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors" onClick={() => setMobileOpen(false)}>Cookie</Link>
              <Link to="/disclaimer" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors" onClick={() => setMobileOpen(false)}>Disclaimer</Link>
              <Link to="/termini" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors" onClick={() => setMobileOpen(false)}>Termini</Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}